import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators'
import store from '@/store/vuex'
import {
	apiDoChatDeSuporte,
	buscarTokenChatSuporte,
	ErroDoChatDeSuporte,
	registrarErroNoChatDeSuporte,
} from '@/api/suporteChat'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import { LojaModule } from '@/store/vuex/loja/LojaStore'
import {
	EstadoDoChat,
	juntarMensagens,
	MensagemDoChat,
	POLLING_PADRAO_MS,
	RespostaDeMensagens,
	SessaoDoChat,
	sessaoViva,
	ultimaMensagemLegivel,
} from '@/shareds/chat-de-suporte'

export type ContextoDaTela = { screen: string; detail?: string }

export type FaseDoChat =
	| { tipo: 'conectando' }
	| { tipo: 'pronto' }
	| { tipo: 'erro'; titulo: string; mensagem: string; podeTentarDeNovo: boolean }

// A URL assinada da mídia vale 10 minutos e muda a cada consulta. Dentro desta janela fica a que já está na tela,
// senão a imagem pisca a cada polling.
const VALIDADE_DA_MIDIA_MS = 8 * 60 * 1000

/**
 * Estado do Almode Chat: sessão, conversa e o que a bolha e o painel mostram. Os dois leem daqui, então
 * minimizar e reabrir nunca recarrega a conversa do zero.
 *
 * Tudo só em memória, como no app Almode: o sessionToken não vai para storage, e recarregar a página pede uma
 * sessão nova quando a pessoa abrir o chat de novo.
 */
@Module({
	name: 'chatDeSuporte',
	store,
	dynamic: true,
	namespaced: true,
})
class ChatDeSuporteStore extends VuexModule {
	/** Id do usuário dono da sessão. Outro usuário logado nunca vê esta conversa. */
	dono: string | null = null
	sessionToken: string | null = null
	expiresAt: string | null = null
	nome = ''
	itens: MensagemDoChat[] = []
	estado: EstadoDoChat | null = null
	naoLidas = 0
	cursor: string | null = null
	etag: string | null = null
	painelAberto = false
	fase: FaseDoChat = { tipo: 'conectando' }
	semConexao = false
	/** "Ocultar" na bolha: ela some até chegar mensagem do suporte com id maior que este. */
	ocultaAte: number | null = null
	/** Tela de onde a pessoa pediu ajuda: vai só na 1ª mensagem depois de abrir pelo menu. */
	contexto: ContextoDaTela | null = null
	/** Erro do diálogo de detalhes, já registrado no suporte: o painel manda como próxima mensagem, uma vez. */
	erroAnexado: string | null = null
	pollMs = POLLING_PADRAO_MS
	ultimaLeituraEnviada = 0
	midiaRecebidaEm: Record<number, number> = {}

	@Mutation
	iniciarSessao({ dono, sessao }: { dono: string; sessao: SessaoDoChat }) {
		if (this.dono !== dono) {
			this.itens = []
			this.estado = null
			this.naoLidas = 0
			this.ocultaAte = null
			this.ultimaLeituraEnviada = 0
			this.midiaRecebidaEm = {}
		}
		this.dono = dono
		this.sessionToken = sessao.sessionToken
		this.expiresAt = sessao.expiresAt
		this.nome = sessao.name || ''
		// O histórico é da pessoa, não da sessão: sessão nova busca tudo de novo.
		this.cursor = null
		this.etag = null
	}

	/** Sessão vencida ou recusada: esquece o token e mantém a conversa na tela. */
	@Mutation
	descartarSessao() {
		this.sessionToken = null
		this.expiresAt = null
		this.etag = null
	}

	@Mutation
	esquecerTudo() {
		this.dono = null
		this.sessionToken = null
		this.expiresAt = null
		this.nome = ''
		this.itens = []
		this.estado = null
		this.naoLidas = 0
		this.cursor = null
		this.etag = null
		this.painelAberto = false
		this.ocultaAte = null
		this.contexto = null
		this.erroAnexado = null
		this.ultimaLeituraEnviada = 0
		this.midiaRecebidaEm = {}
	}

	/** Abrir o painel busca o histórico inteiro de novo, sem perder o que já está na tela. */
	@Mutation
	reiniciarCursor() {
		this.cursor = null
		this.etag = null
	}

	@Mutation
	aplicarResposta({ resposta, etag, agora }: { resposta: RespostaDeMensagens; etag: string | null; agora: number }) {
		const anteriores = new Map(this.itens.map(mensagem => [mensagem.id, mensagem]))
		const recebidaEm = { ...this.midiaRecebidaEm }
		const novas = (resposta.items || []).map(mensagem => {
			const anterior = anteriores.get(mensagem.id)
			if (mensagem.media && anterior && anterior.media && agora - (recebidaEm[mensagem.id] || 0) < VALIDADE_DA_MIDIA_MS) {
				return { ...mensagem, media: anterior.media }
			}
			if (mensagem.media) recebidaEm[mensagem.id] = agora
			return mensagem
		})
		this.itens = juntarMensagens(this.itens, novas)
		this.midiaRecebidaEm = recebidaEm
		this.etag = etag
		this.cursor = resposta.cursor
		this.naoLidas = resposta.unread || 0
		this.estado = resposta.state || null
	}

	@Mutation
	abrirPainel(contexto: ContextoDaTela | null) {
		this.painelAberto = true
		this.contexto = contexto
	}

	@Mutation
	fecharPainel() {
		this.painelAberto = false
	}

	@Mutation
	setFase(fase: FaseDoChat) {
		this.fase = fase
	}

	@Mutation
	setSemConexao(semConexao: boolean) {
		this.semConexao = semConexao
	}

	@Mutation
	ocultarAte(id: number | null) {
		this.ocultaAte = id
	}

	@Mutation
	usarContexto() {
		this.contexto = null
	}

	@Mutation
	anexarErro(reportId: string | null) {
		this.erroAnexado = reportId
	}

	@Mutation
	setPollMs(pollMs: number) {
		this.pollMs = pollMs
	}

	@Mutation
	registrarLeitura(upTo: number) {
		this.ultimaLeituraEnviada = upTo
		this.naoLidas = 0
	}

	@Mutation
	desfazerLeitura() {
		this.ultimaLeituraEnviada = 0
	}
}

export const ChatDeSuporteModule = getModule(ChatDeSuporteStore)

let conexaoEmAndamento: Promise<void> | null = null
let intervaloCarregado = false

/** Loja da venda aberta, ou a selecionada. O backend só usa se for uma das lojas do usuário logado. */
export function lojaEmUso(): string | null {
	return (VendaModule.lojaDaVenda && VendaModule.lojaDaVenda.id)
		|| (LojaModule.lojaSelecionada && LojaModule.lojaSelecionada.id)
		|| null
}

export function sessaoVivaDe(dono: string | null, agora = Date.now()): boolean {
	return !!dono
		&& ChatDeSuporteModule.dono === dono
		&& sessaoViva(ChatDeSuporteModule.sessionToken, ChatDeSuporteModule.expiresAt, agora)
}

/**
 * Sessão viva para o dono. Só roda quando a pessoa abre o chat: cada sessão registra um contato no suporte, então
 * nunca abre sozinha. Duas chamadas ao mesmo tempo dividem a mesma conexão (o token é de uso único).
 */
export function conectarChatDeSuporte(dono: string): Promise<void> {
	if (sessaoVivaDe(dono)) return Promise.resolve()
	if (!conexaoEmAndamento) {
		conexaoEmAndamento = (async () => {
			const { token } = await buscarTokenChatSuporte(lojaEmUso())
			const sessao = await apiDoChatDeSuporte.abrirSessao(token)
			ChatDeSuporteModule.iniciarSessao({ dono, sessao })
		})().finally(() => {
			conexaoEmAndamento = null
		})
	}
	return conexaoEmAndamento
}

/** Intervalo rápido do polling, do /config do suporte. Na falha fica o padrão de 3 s. */
export async function carregarIntervaloDoChat(): Promise<void> {
	if (intervaloCarregado) return
	try {
		const configuracao = await apiDoChatDeSuporte.configuracao()
		const pollMs = configuracao ? Number(configuracao.pollMs) : NaN
		if (Number.isFinite(pollMs) && pollMs >= 1000) ChatDeSuporteModule.setPollMs(pollMs)
		intervaloCarregado = true
	} catch {
		// fica o padrão
	}
}

/** Uma rodada do polling (cursor + ETag). 304 é a resposta normal sem novidade. Os erros sobem. */
export async function consultarChatDeSuporte(): Promise<void> {
	const token = ChatDeSuporteModule.sessionToken
	if (!token) return
	const resposta = await apiDoChatDeSuporte.mensagens(token, ChatDeSuporteModule.cursor, ChatDeSuporteModule.etag)
	if (ChatDeSuporteModule.sessionToken !== token || resposta.status === 304 || !resposta.dados) return
	ChatDeSuporteModule.aplicarResposta({ resposta: resposta.dados, etag: resposta.etag, agora: Date.now() })
}

/** Painel aberto e aba visível: o que chegou do suporte foi visto. */
export function marcarChatDeSuporteComoLido(): void {
	const token = ChatDeSuporteModule.sessionToken
	const upTo = ultimaMensagemLegivel(ChatDeSuporteModule.itens)
	if (!token || upTo <= ChatDeSuporteModule.ultimaLeituraEnviada) return
	ChatDeSuporteModule.registrarLeitura(upTo)
	apiDoChatDeSuporte.marcarComoLidas(token, upTo).catch(() => ChatDeSuporteModule.desfazerLeitura())
}

/** "Falar com o suporte" (com a tela de origem) ou a bolha (sem contexto: a conversa já está andando). */
export function abrirChatDeSuporte(contexto: ContextoDaTela | null): void {
	ChatDeSuporteModule.abrirPainel(contexto)
}

/**
 * "Falar com o suporte" no diálogo do erro: o backend do ERP registra o erro no suporte e o chat abre com ele anexado
 * à próxima mensagem. Falha do registro sobe para o diálogo, e o chat não abre.
 */
export async function falarComOSuporteSobreOErro(
	codigo: string,
	mensagemExibida: string,
	contexto: ContextoDaTela,
): Promise<void> {
	const { reportId } = await registrarErroNoChatDeSuporte({
		codigo,
		tela: contexto.screen,
		mensagemExibida,
		lojaId: lojaEmUso(),
	})
	ChatDeSuporteModule.anexarErro(reportId)
	ChatDeSuporteModule.abrirPainel(contexto)
}

/** A tela de onde a pessoa pediu ajuda vai junto na 1ª mensagem, para o suporte saber onde ela está. */
export function contextoDaTela(rota: { name?: string | null; path: string }): ContextoDaTela {
	return { screen: String(rota.name || document.title), detail: rota.path }
}

export function faseDeErro(erro: unknown): FaseDoChat {
	if (erro instanceof ErroDoChatDeSuporte) {
		return {
			tipo: 'erro',
			titulo: 'Não foi possível abrir o chat',
			mensagem: erro.message,
			podeTentarDeNovo: erro.status >= 429 || erro.status === 401,
		}
	}
	const status = (erro as { response?: { status?: number } } | null)?.response?.status
	if (status === 503) {
		return {
			tipo: 'erro',
			titulo: 'Chat indisponível',
			mensagem: 'O chat de suporte ainda não está configurado neste ambiente.',
			podeTentarDeNovo: false,
		}
	}
	return {
		tipo: 'erro',
		titulo: 'Sem conexão com o chat',
		mensagem: 'Confira a internet e tente de novo.',
		podeTentarDeNovo: true,
	}
}
